import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Coffee } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * The only in-app page a sales agent has. Every other route is a till screen
 * for a tenant they do not belong to, so the API would refuse it - sending them
 * home is friendlier than letting each one load and fail.
 */
const SALES_ALLOWED_PATHS = ['/'];

/**
 * What a super admin can reach before opening a business. Every other route is
 * a till screen, and with no tenant scoped there is nothing for one to load -
 * a bookmark or a typed URL would only render an empty page, so it goes home
 * instead. Opening a business lifts this entirely.
 */
const SUPER_ADMIN_CONSOLE_PATHS = ['/', '/contact'];

function ProtectedRoute({ children, requiredRole = null }) {
  const { isAuthenticated, user, loading, tenantId } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cafe-50 to-cafe-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cafe-600 rounded-full mb-4 animate-pulse">
            <Coffee className="h-8 w-8 text-white" />
          </div>
          <p className="text-gray-600">{t('Loading...')}</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role === 'sales' && !SALES_ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  if (
    user?.role === 'super_admin' &&
    !tenantId &&
    !SUPER_ADMIN_CONSOLE_PATHS.includes(location.pathname)
  ) {
    return <Navigate to="/" replace />;
  }

  // Check role-based access if required
  if (requiredRole && user?.role !== requiredRole) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cafe-50 to-cafe-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <Coffee className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('Access Denied')}</h1>
            <p className="text-gray-600 mb-4">
              {t("You don't have permission to access this page.")}
            </p>
            <p className="text-sm text-gray-500">
              {t('Required role:')} <span className="font-medium">{requiredRole}</span><br />
              {t('Your role:')} <span className="font-medium">{user?.role || t('Unknown')}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;